import { useEffect, useState } from 'react';

export type Breakpoint = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
export type Breakpoints = {
  [K in Breakpoint]: number;
};

export const breakpoints: Breakpoints = {
  xsmall: 320,
  small: 600,
  medium: 850,
  large: 1280,
  xlarge: 1600,
  xxlarge: 1920,
};

export const setBreakPoints = (partial: Partial<Breakpoints>) => {
  Object.assign(breakpoints, partial);
};

export const getBreakpoint = () => {
  const sorted = Object.entries(breakpoints).sort((a, b) => a[1] - b[1]) as [Breakpoint, number][];
  const result = sorted.reduce((previous, [breakpoint, width]) => {
    if (window.innerWidth < width) return previous;
    return breakpoint;
  }, sorted[0][0]);
  return result;
};

export type WindowResizeEventHandler = (event: UIEvent) => void;
const createObserver = () => {
  const callbacks: Set<WindowResizeEventHandler> = new Set();

  const observe = (callback: WindowResizeEventHandler) => {
    callbacks.add(callback);

    return () => {
      callbacks.delete(callback);
    };
  };

  const unobserve = (callback: WindowResizeEventHandler) => {
    callbacks.delete(callback);
  };

  const disconnect = () => {
    window.removeEventListener('resize', onResize);
  };

  const onResize = (e: UIEvent) => {
    callbacks.forEach((callback) => callback(e));
  };

  window.addEventListener('resize', onResize);

  return {
    observe,
    unobserve,
    disconnect,
  };
};

export const observer = createObserver();

export const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState(getBreakpoint());
  useEffect(() => {
    const onResize = () => {
      setBreakpoint((old) => {
        const current = getBreakpoint();
        return old !== current ? current : old;
      });
    };
    return observer.observe(onResize);
  }, []);

  return breakpoint;
};

const getWidth = (breakpoint: Breakpoint | number) => {
  if (typeof breakpoint === 'number') return breakpoint;
  return breakpoints[breakpoint];
};

export const useIsSmallerThan = (breakpoint: Breakpoint | number) =>
  getWidth(useBreakpoint()) < getWidth(breakpoint);

export const isMobile = () => getWidth(getBreakpoint()) < getWidth('large');

export const useIsMobile = () => useIsSmallerThan('large');

export const onWindowResizeEvent = (callback: WindowResizeEventHandler) => {
  observer.observe(callback);
  return () => observer.unobserve(callback);
};
